import { ctx, W } from '../canvas.js';
import { state } from '../state.js';
import { UI_FONT } from '../config.js';
import { pixelRect } from '../utils.js';

/** Half-width of the lock-on brackets, and how far above the feet they sit. */
const MARK_HALF = 30;
const MARK_UP = 46;
const MARK_DOWN = 8;
/** Length of each bracket arm. */
const ARM = 9;

/** True while the rival is painted and the paint has not run out. */
export function paintActive() {
  return state.paintedFor >= 0 && state.clock < state.paintedUntil;
}

/**
 * The drone and what it is looking at.
 *
 * Deliberately not part of render/background.js: that whole module is gated on
 * the mode having a sky, and a killstreak that vanishes in CLASSIC would be a
 * special that does nothing. This one is a weapon, so it draws whenever it exists.
 */
export function drawDrone() {
  if (state.drone) drawBody(state.drone);
  if (state.bomb) drawBomb(state.bomb);
  if (paintActive()) drawPaint();
}

/**
 * La carga, cayendo. Con su estela y su luz roja: a este tamaño lo que la hace legible es la
 * luz, y el jugador tiene que ver de dónde le viene.
 */
function drawBomb(b) {
  const x = Math.round(b.x);
  const y = Math.round(b.y);
  const blink = Math.floor(state.clock / 90) % 2 === 0;

  ctx.globalAlpha = 0.35;
  pixelRect(x, y - 8, 1, 5, '#9aa3b8');
  ctx.globalAlpha = 1;

  pixelRect(x - 4, y - 4, 8, 1, '#39405a');
  pixelRect(x - 3, y - 3, 6, 6, '#232a40');
  pixelRect(x - 1, y - 2, 2, 2, blink ? '#ff5a5a' : '#5a2020');
}

/** A quadcopter, small enough to read as a silhouette and no bigger. */
function drawBody(d) {
  const x = Math.round(d.x);
  const y = Math.round(d.y);
  // The rotors are drawn as bars whose width flickers, which at this size reads
  // as spinning without a single rotating pixel.
  const spin = Math.sin(state.clock / 26) * 2;

  const target = state.players[state.paintedFor];

  // The search cone first, so the drone sits on top of it. Only while there is
  // still somebody down there to look at.
  if (target && target.alive && state.paintedFor >= 0) {
    ctx.save();
    ctx.globalAlpha = 0.1 + 0.05 * Math.sin(state.clock / 180);
    ctx.fillStyle = '#6fe0cd';
    ctx.beginPath();
    ctx.moveTo(x, y + 6);
    ctx.lineTo(target.x - 26, target.y);
    ctx.lineTo(target.x + 26, target.y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // Rotor wash.
  ctx.globalAlpha = 0.3;
  pixelRect(x - 16, y - 8, 32, 1, '#6fe0cd');
  ctx.globalAlpha = 1;

  // Rotors, arms, body.
  pixelRect(x - 14 - spin, y - 5, 11 + spin, 2, '#5a6480');
  pixelRect(x + 3 - spin, y - 5, 11 + spin, 2, '#5a6480');
  pixelRect(x - 8, y - 3, 16, 2, '#39405a');
  pixelRect(x - 1, y - 3, 2, 7, '#39405a');
  pixelRect(x - 5, y - 1, 10, 7, '#232a40');

  // Its eye, blinking. A drone that stares without blinking is not unsettling
  // enough for what it is about to do.
  const lit = Math.floor(state.clock / 300) % 2 === 0;
  pixelRect(x - 1, y + 2, 2, 2, lit ? '#ff5a5a' : '#5a2020');
}

/**
 * The mark over the painted gorilla: four corner brackets, a label and the range.
 * The range is the whole point — a UAV exists to tell you where somebody is, and
 * in a game with wind and no rangefinder the useful form of that is a distance.
 */
function drawPaint() {
  const t = state.players[state.paintedFor];
  if (!t || !t.alive) return;

  const other = state.players[1 - state.paintedFor];
  const x = Math.round(t.x);
  const y = Math.round(t.y);
  const blink = Math.floor(state.clock / 400) % 2 === 0;

  ctx.save();
  ctx.strokeStyle = blink ? '#6fe0cd' : '#3f8f84';
  ctx.lineWidth = 2;

  // Corner brackets rather than a box: a closed box reads as scenery, a set of
  // brackets reads as a lock-on.
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const cx = x + sx * MARK_HALF;
      const cy = y + (sy < 0 ? -MARK_UP : MARK_DOWN);

      ctx.beginPath();
      ctx.moveTo(cx - sx * ARM, cy);
      ctx.lineTo(cx, cy);
      ctx.lineTo(cx, cy - sy * ARM);
      ctx.stroke();
    }
  }

  ctx.font = `700 10px ${UI_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#6fe0cd';

  // One line, not two: stacked, the range landed on the gorilla's own head and
  // became the one thing on screen nobody could read.
  //
  // Canvas pixels, read as metres. It is a made-up unit either way, and "764" on
  // its own tells the player nothing they can aim with.
  const range = other ? 'UAV ' + Math.round(Math.abs(t.x - other.x)) + ' m' : 'UAV';
  ctx.fillText(range, x, y - MARK_UP - 16);

  ctx.restore();
}
